/**
 * Assessments Page
 *
 * List view for assessments with filtering by operator, SOP, date range,
 * and score range. Links to assessment detail and new assessment form.
 */

import { useState, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  ClipboardCheck,
  Plus,
  Filter,
  ChevronLeft,
  ChevronRight,
  Eye,
  Calendar,
  X,
  Scan,
  Loader2,
  Camera,
  Video,
  AlertTriangle,
  Layers,
  Clock,
} from 'lucide-react';
import { useAssessments } from '../../hooks/useAssessments';
import {
  useInFlightTriggers,
  type InFlightTrigger,
  type InFlightStatus,
} from '../../hooks/useInFlightTriggers';
import { useOperatorLookup } from '../../hooks/useOperators';
import { useSopLookup } from '../../hooks/useSOPs';
import { useDepartmentLookup } from '../../hooks/useDepartments';
import { VideoAnalysisModal } from './components/VideoAnalysisModal';
import { getScoreColor } from '../../lib/score-colors';
import { useDocumentTitle } from '../../lib/hooks/useDocumentTitle';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';

// In-progress strip status pill colors. Mirrors the statusConfig map in
// TriggerDetailPage.tsx:64-72 (kept inline to avoid a refactor that's
// out of scope for this chunk; if/when a third surface needs it, extract
// to a shared util at lib/trigger-status.ts).
const inFlightStatusConfig: Record<InFlightStatus, { label: string; color: string; icon: typeof Clock }> = {
  pending: { label: 'Pending', color: 'bg-gray-100 text-gray-600', icon: Clock },
  pulling_video: { label: 'Pulling Video', color: 'bg-blue-50 text-blue-600', icon: Video },
  analyzing: { label: 'Analyzing', color: 'bg-amber-50 text-amber-600', icon: Loader2 },
  pending_review: { label: 'Awaiting Operator ID', color: 'bg-orange-50 text-orange-600', icon: AlertTriangle },
  pending_section_review: { label: 'Awaiting Section', color: 'bg-amber-50 text-amber-600', icon: Layers },
};

function formatElapsed(triggeredAt: string): string {
  const start = new Date(triggeredAt).getTime();
  const elapsedMs = Date.now() - start;
  if (Number.isNaN(elapsedMs) || elapsedMs < 0) return 'just now';
  const seconds = Math.floor(elapsedMs / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

// Debounce hook
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useMemo(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

export function AssessmentsPage() {
  useDocumentTitle('Assessments');
  // Video analysis modal state
  const [showVideoModal, setShowVideoModal] = useState(false);

  // Filter state
  const [operatorId, setOperatorId] = useState<string>('');
  const [sopId, setSopId] = useState<string>('');
  const [departmentId, setDepartmentId] = useState<string>('');
  const [assessmentType, setAssessmentType] = useState<string>('');
  const [minScore, setMinScore] = useState<string>('');
  const [maxScore, setMaxScore] = useState<string>('');
  const [page, setPage] = useState(1);
  const [limit] = useState(10);

  // Debounce score filters
  const debouncedMinScore = useDebounce(minScore, 300);
  const debouncedMaxScore = useDebounce(maxScore, 300);

  // Phase 30.1.4: In-progress strip — polls in-flight triggers via the new
  // ?status= filter on /api/assessments/triggers. Adaptive polling (5s when
  // rows present, 30s when idle) — see useInFlightTriggers.ts.
  const { data: inFlightData } = useInFlightTriggers();
  const inFlightTriggers = inFlightData?.data ?? [];

  // Build query options
  const queryOptions = useMemo(() => {
    const options: {
      operatorId?: string;
      sopId?: string;
      departmentId?: string;
      assessmentType?: 'auto' | 'manual';
      minScore?: number;
      maxScore?: number;
      page: number;
      limit: number;
      sortBy: string;
      sortOrder: 'desc';
    } = { page, limit, sortBy: 'assessedAt', sortOrder: 'desc' };

    if (operatorId) options.operatorId = operatorId;
    if (sopId) options.sopId = sopId;
    if (departmentId) options.departmentId = departmentId;
    if (assessmentType === 'auto' || assessmentType === 'manual') {
      options.assessmentType = assessmentType;
    }
    if (debouncedMinScore) options.minScore = parseInt(debouncedMinScore, 10);
    if (debouncedMaxScore) options.maxScore = parseInt(debouncedMaxScore, 10);

    return options;
  }, [operatorId, sopId, departmentId, assessmentType, debouncedMinScore, debouncedMaxScore, page, limit]);

  // Fetch data
  const { data: assessmentsData, isLoading, error } = useAssessments(queryOptions);
  const { data: operators } = useOperatorLookup();
  const { data: sops } = useSopLookup();
  const { data: departments } = useDepartmentLookup();

  const assessments = assessmentsData?.data ?? [];
  const pagination = assessmentsData?.pagination;
  const totalPages = pagination?.totalPages ?? 1;
  const total = pagination?.total ?? 0;

  const showingFrom = total === 0 ? 0 : (page - 1) * limit + 1;
  const showingTo = Math.min(page * limit, total);

  const handleClearFilters = useCallback(() => {
    setOperatorId('');
    setSopId('');
    setDepartmentId('');
    setAssessmentType('');
    setMinScore('');
    setMaxScore('');
    setPage(1);
  }, []);

  const hasActiveFilters = Boolean(operatorId || sopId || departmentId || assessmentType || minScore || maxScore);


  // Generate page numbers
  const getPageNumbers = () => {
    const pages: (number | 'ellipsis')[] = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (page > 3) pages.push('ellipsis');
      for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) {
        pages.push(i);
      }
      if (page < totalPages - 2) pages.push('ellipsis');
      pages.push(totalPages);
    }
    return pages;
  };

  const formatDate = (date: string | Date) => {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }).format(new Date(date));
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-1">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-guild-green to-guild-green/80 shadow-lg shadow-guild-green/20">
              <ClipboardCheck size={20} className="text-white" strokeWidth={2.5} />
            </div>
            <h1 className="text-2xl font-bold tracking-tight text-gray-900">
              Assessments
            </h1>
          </div>
          <p className="text-sm text-gray-500 ml-[52px]">
            View and manage operator performance assessments
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowVideoModal(true)}
            className="
              inline-flex items-center gap-2 rounded-xl
              border border-guild-green/30 bg-guild-green/5 px-5 py-2.5
              text-sm font-semibold text-guild-green
              transition-all duration-200
              hover:bg-guild-green/10 hover:border-guild-green/50
              active:scale-[0.98]
            "
          >
            <Scan size={18} strokeWidth={2.5} />
            Analyze Video
          </button>
          <Link
            to="/assessments/new-v2"
            className="
              inline-flex items-center gap-2 rounded-xl
              bg-guild-green px-5 py-2.5
              text-sm font-semibold text-white
              shadow-lg shadow-guild-green/25
              transition-all duration-200
              hover:bg-guild-green/90 hover:shadow-xl hover:shadow-guild-green/30
              active:scale-[0.98]
            "
          >
            <Plus size={18} strokeWidth={2.5} />
            New Assessment
          </Link>
        </div>
      </div>

      {/* Phase 30.1.4: In Progress strip — only renders when there's at least one
          in-flight trigger. Capped at 5 visible rows (any extras hinted via
          "+ N more"). Each row links to the trigger detail page so the user can
          watch it move through the pipeline. */}
      {inFlightTriggers.length > 0 && (
        <div className="rounded-2xl border border-guild-green/30 bg-guild-mint/20 p-4 shadow-sm">
          <div className="flex items-center gap-2 mb-3">
            <Loader2 size={16} className="animate-spin text-guild-green" strokeWidth={2.5} />
            <h2 className="text-sm font-bold tracking-tight text-guild-green uppercase">
              In progress
            </h2>
            <span className="text-xs text-guild-green/70">
              ({inFlightTriggers.length} {inFlightTriggers.length === 1 ? 'trigger' : 'triggers'})
            </span>
          </div>
          <div className="space-y-2">
            {inFlightTriggers.slice(0, 5).map((trigger: InFlightTrigger) => {
              const statusInfo = inFlightStatusConfig[trigger.status];
              const StatusIcon = statusInfo.icon;
              return (
                <Link
                  key={trigger.id}
                  to={`/assessments/triggers/${trigger.id}`}
                  className="
                    flex items-center justify-between gap-3
                    rounded-xl bg-white px-4 py-2.5
                    border border-guild-mint/40
                    transition-all duration-150
                    hover:border-guild-green/40 hover:shadow-sm
                  "
                >
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${statusInfo.color}`}>
                      <StatusIcon
                        size={11}
                        className={trigger.status === 'analyzing' ? 'animate-spin' : ''}
                      />
                      {statusInfo.label}
                    </span>
                    <span className="text-xs text-gray-500 font-mono truncate">
                      {trigger.id.slice(0, 8)}
                    </span>
                    {(trigger.cameraName || trigger.cameraId) && (
                      <span className="hidden sm:flex items-center gap-1 text-xs text-gray-500 truncate">
                        <Camera size={12} className="text-gray-400 flex-shrink-0" />
                        <span className="truncate">{trigger.cameraName || trigger.cameraId}</span>
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <span className="text-xs text-gray-500">
                      {formatElapsed(trigger.triggeredAt)}
                    </span>
                    <span className="text-sm font-semibold text-guild-green">
                      View →
                    </span>
                  </div>
                </Link>
              );
            })}
            {inFlightTriggers.length > 5 && (
              <p className="text-xs text-guild-green/70 text-center pt-1">
                + {inFlightTriggers.length - 5} more in-flight triggers
              </p>
            )}
          </div>
        </div>
      )}

      {/* Filter Bar */}
      <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:flex-wrap">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Filter size={16} />
            <span>Filters:</span>
          </div>

          {/* Operator Filter */}
          <select
            value={operatorId}
            onChange={(e) => { setOperatorId(e.target.value); setPage(1); }}
            className="appearance-none rounded-lg border border-gray-200 bg-white py-2 px-3 text-sm text-gray-700 transition-colors hover:border-gray-300 focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20"
          >
            <option value="">All Operators</option>
            {operators?.map((op) => (
              <option key={op.id} value={op.id}>{op.fullName}</option>
            ))}
          </select>

          {/* SOP Filter */}
          <select
            value={sopId}
            onChange={(e) => { setSopId(e.target.value); setPage(1); }}
            className="appearance-none rounded-lg border border-gray-200 bg-white py-2 px-3 text-sm text-gray-700 transition-colors hover:border-gray-300 focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20"
          >
            <option value="">All SOPs</option>
            {sops?.map((sop) => (
              <option key={sop.id} value={sop.id}>{sop.title}</option>
            ))}
          </select>

          {/* Department Filter */}
          <select
            value={departmentId}
            onChange={(e) => { setDepartmentId(e.target.value); setPage(1); }}
            className="appearance-none rounded-lg border border-gray-200 bg-white py-2 px-3 text-sm text-gray-700 transition-colors hover:border-gray-300 focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20"
          >
            <option value="">All Departments</option>
            {departments?.map((dept) => (
              <option key={dept.id} value={dept.id}>{dept.name}</option>
            ))}
          </select>

          {/* Type Filter */}
          <select
            value={assessmentType}
            onChange={(e) => { setAssessmentType(e.target.value); setPage(1); }}
            className="appearance-none rounded-lg border border-gray-200 bg-white py-2 px-3 text-sm text-gray-700 transition-colors hover:border-gray-300 focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20"
          >
            <option value="">All Types</option>
            <option value="manual">Manual</option>
            <option value="auto">Automated</option>
          </select>

          {/* Score Range */}
          <div className="flex items-center gap-2">
            <input
              type="number"
              placeholder="Min Score"
              value={minScore}
              onChange={(e) => { setMinScore(e.target.value); setPage(1); }}
              min="0"
              max="100"
              className="w-24 rounded-lg border border-gray-200 bg-white py-2 px-3 text-sm text-gray-700 transition-colors hover:border-gray-300 focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20"
            />
            <span className="text-gray-400">-</span>
            <input
              type="number"
              placeholder="Max Score"
              value={maxScore}
              onChange={(e) => { setMaxScore(e.target.value); setPage(1); }}
              min="0"
              max="100"
              className="w-24 rounded-lg border border-gray-200 bg-white py-2 px-3 text-sm text-gray-700 transition-colors hover:border-gray-300 focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20"
            />
          </div>

          {/* Clear Filters */}
          {hasActiveFilters && (
            <button
              onClick={handleClearFilters}
              className="flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700"
            >
              <X size={14} />
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="rounded-2xl border border-gray-100 bg-white shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-16">
            <LoadingSpinner size="lg" />
            <p className="mt-4 text-sm text-gray-500">Loading assessments...</p>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-16">
            <p className="text-sm font-medium text-gray-900">Failed to load assessments</p>
          </div>
        ) : assessments.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-gray-100 to-gray-50">
              <ClipboardCheck size={32} className="text-gray-400" />
            </div>
            <p className="mt-4 text-sm font-medium text-gray-900">
              {hasActiveFilters ? 'No assessments found' : 'No assessments yet'}
            </p>
            <p className="mt-1 text-sm text-gray-500">
              {hasActiveFilters
                ? 'Try adjusting your filters.'
                : 'Create your first assessment to get started.'}
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50/50">
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Operator</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">SOP</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Type</th>
                    <th className="px-6 py-4 text-center text-xs font-semibold uppercase tracking-wider text-gray-500">Compliance</th>
                    <th className="px-6 py-4 text-center text-xs font-semibold uppercase tracking-wider text-gray-500">Efficiency</th>
                    <th className="px-6 py-4 text-center text-xs font-semibold uppercase tracking-wider text-gray-500">Safety</th>
                    <th className="px-6 py-4 text-center text-xs font-semibold uppercase tracking-wider text-gray-500">Overall</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Date</th>
                    <th className="px-6 py-4 text-right text-xs font-semibold uppercase tracking-wider text-gray-500">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {assessments.map((assessment) => (
                    <tr key={assessment.id} className="group transition-colors hover:bg-guild-mint/5">
                      <td className="px-6 py-4">
                        <div className="font-semibold text-gray-900">
                          {assessment.operator.firstName} {assessment.operator.lastName}
                        </div>
                        <div className="text-xs text-gray-500">{assessment.operator.employeeId}</div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-gray-700 line-clamp-2">{assessment.sop.title}</span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
                          assessment.assessmentType === 'auto'
                            ? 'bg-blue-50 text-blue-700'
                            : 'bg-purple-50 text-purple-700'
                        }`}>
                          {assessment.assessmentType === 'auto' ? 'Auto' : 'Manual'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className={`inline-flex min-w-[48px] justify-center rounded-lg px-2 py-1 text-sm font-semibold ${getScoreColor(assessment.complianceScore, 'badge')}`}>
                          {assessment.complianceScore.toFixed(0)}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className={`inline-flex min-w-[48px] justify-center rounded-lg px-2 py-1 text-sm font-semibold ${getScoreColor(assessment.efficiencyScore, 'badge')}`}>
                          {assessment.efficiencyScore.toFixed(0)}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className={`inline-flex min-w-[48px] justify-center rounded-lg px-2 py-1 text-sm font-semibold ${getScoreColor(assessment.safetyScore, 'badge')}`}>
                          {assessment.safetyScore.toFixed(0)}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className={`inline-flex min-w-[48px] justify-center rounded-lg px-2.5 py-1 text-sm font-bold ${getScoreColor(assessment.overallScore, 'badge')}`}>
                          {assessment.overallScore.toFixed(0)}
                        </span>
                        {/* A10.7b: step coverage sub-line. Rendered only when both
                            counts are known — NULL means pre-A10 or manual, where
                            there is no coverage to show. */}
                        {typeof assessment.observedStepCount === 'number' &&
                          typeof assessment.totalStepCount === 'number' &&
                          assessment.totalStepCount > 0 && (
                            <div className="mt-1 text-[11px] text-gray-500">
                              {Math.round(
                                (assessment.observedStepCount / assessment.totalStepCount) * 100
                              )}
                              % observed
                            </div>
                          )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-1.5 text-sm text-gray-600">
                          <Calendar size={14} className="text-gray-400" />
                          {formatDate(assessment.assessedAt)}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end">
                          <Link
                            to={`/assessments/${assessment.id}`}
                            className="rounded-lg p-2 text-gray-400 transition-all hover:bg-guild-mint/20 hover:text-guild-green"
                            title="View details"
                          >
                            <Eye size={16} />
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex flex-col items-center justify-between gap-4 border-t border-gray-100 bg-gray-50/30 px-6 py-4 sm:flex-row">
              <p className="text-sm text-gray-500">
                Showing <span className="font-medium text-gray-700">{showingFrom}</span> to{' '}
                <span className="font-medium text-gray-700">{showingTo}</span> of{' '}
                <span className="font-medium text-gray-700">{total}</span> assessments
              </p>

              <div className="flex items-center gap-1">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-white hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <ChevronLeft size={16} />
                  Previous
                </button>

                <div className="flex items-center gap-1 px-2">
                  {getPageNumbers().map((pageNum, idx) =>
                    pageNum === 'ellipsis' ? (
                      <span key={`ellipsis-${idx}`} className="px-2 text-gray-400">...</span>
                    ) : (
                      <button
                        key={pageNum}
                        onClick={() => setPage(pageNum)}
                        className={`min-w-[36px] rounded-lg px-3 py-2 text-sm font-medium transition-all ${
                          page === pageNum
                            ? 'bg-guild-green text-white shadow-sm'
                            : 'text-gray-600 hover:bg-white hover:text-gray-900'
                        }`}
                      >
                        {pageNum}
                      </button>
                    )
                  )}
                </div>

                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-white hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Next
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Video Analysis Modal */}
      <VideoAnalysisModal isOpen={showVideoModal} onClose={() => setShowVideoModal(false)} />
    </div>
  );
}

export default AssessmentsPage;
